/**
 * Ops page chrome — one MUI layout language for all AppShell screens.
 * Layout stacks for xs/sm; side-by-side actions from sm; denser from lg.
 */
import type { ReactNode } from "react";
import { Alert, Box, Breadcrumbs, Button, CircularProgress, Link as MuiLink, Stack, Typography } from "@mui/material";
import { Link as RouterLink } from "react-router-dom";

export function PageHeader({
  title,
  subtitle,
  actions,
  crumbs,
}: {
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  crumbs?: { label: string; to?: string }[];
}) {
  return (
    <Stack spacing={{ xs: 1, sm: 1.25, md: 1.5 }} sx={{ mb: { xs: 2, sm: 2.5, lg: 3 } }}>
      {crumbs && crumbs.length > 0 ? (
        <Breadcrumbs aria-label="breadcrumb" sx={{ fontSize: { xs: "0.75rem", md: "0.8rem" } }}>
          {crumbs.map((c, i) =>
            c.to ? (
              <MuiLink key={`${c.label}-${i}`} component={RouterLink} to={c.to} underline="hover" color="inherit">
                {c.label}
              </MuiLink>
            ) : (
              <Typography key={`${c.label}-${i}`} color="text.primary" variant="body2">
                {c.label}
              </Typography>
            ),
          )}
        </Breadcrumbs>
      ) : null}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1.25, sm: 1.5, md: 2 }}
        sx={{
          justifyContent: "space-between",
          alignItems: { xs: "stretch", sm: "flex-start", md: "center" },
        }}
      >
        <Box sx={{ minWidth: 0, flex: "1 1 auto" }}>
          <Typography
            variant="h2"
            sx={{
              wordBreak: "break-word",
              fontSize: { xs: "1.15rem", sm: "1.25rem", lg: "1.35rem" },
            }}
          >
            {title}
          </Typography>
          {subtitle ? (
            <Typography
              color="text.secondary"
              sx={{ mt: 0.5, fontSize: { xs: "0.8rem", md: "0.875rem" }, maxWidth: { lg: 720 } }}
            >
              {subtitle}
            </Typography>
          ) : null}
        </Box>
        {actions ? (
          <Stack
            direction="row"
            spacing={1}
            useFlexGap
            sx={{
              flexWrap: "wrap",
              flexShrink: 0,
              width: { xs: "100%", sm: "auto" },
              "& > *": { flex: { xs: "1 1 auto", sm: "0 0 auto" } },
            }}
          >
            {actions}
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
}

export function PageState({
  status,
  error,
  emptyTitle,
  emptyHint,
  onRetry,
  children,
}: {
  status: "loading" | "error" | "empty" | "success";
  error?: string;
  emptyTitle?: string;
  emptyHint?: string;
  onRetry?: () => void;
  children?: ReactNode;
}) {
  if (status === "loading") {
    return (
      <Box sx={{ display: "grid", placeItems: "center", py: 8, gap: 2 }}>
        <CircularProgress size={36} />
        <Typography color="text.secondary">Loading…</Typography>
      </Box>
    );
  }
  if (status === "error") {
    return (
      <Alert
        severity="error"
        action={
          onRetry ? (
            <Button color="inherit" size="small" onClick={onRetry}>
              Retry
            </Button>
          ) : undefined
        }
        sx={{ my: 2 }}
      >
        {error || "Something went wrong"}
      </Alert>
    );
  }
  if (status === "empty") {
    return (
      <Box sx={{ py: 6, textAlign: "center" }}>
        <Typography variant="h3" gutterBottom>
          {emptyTitle || "Nothing here yet"}
        </Typography>
        {emptyHint ? <Typography color="text.secondary">{emptyHint}</Typography> : null}
      </Box>
    );
  }
  return <>{children}</>;
}
